/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../config/api';

export const CLASS_OF_SERVICES_ENDPOINT = '/api/telephony/class-of-services';
export const FEATURE_CODE_COS_ENDPOINT = '/api/telephony/feature-categories';
export const DIAL_RULE_RESTRICTIONS_ENDPOINT = '/api/telephony/dial-rule-restrictions';
export const ARS_ENDPOINT = '/api/telephony/ars';

export interface ApiPagination {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  pagination?: ApiPagination;
}

export interface ApiErrorResponse {
  success: false;
  message?: string;
  [key: string]: unknown;
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse | undefined | null;

const normaliseHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
};

export async function requestTelephonyModule<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<ApiSuccessResponse<T>> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normaliseHeaders(options.headers)
      }
    }
  );

  let payload: ApiResponse<T>;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (typeof payload === 'object' && payload && 'success' in payload && payload.success === true) {
    return payload as ApiSuccessResponse<T>;
  }

  // If the endpoint responded with no content but without errors, synthesise a success envelope.
  return {
    success: true,
    data: undefined as T
  };
}

